import React, { useState, useEffect } from "react";
import { API_URLS } from "../../config/apiUrls";
import {
  Button,
  // Grid, // REMOVIDO: Eliminando o componente Grid que causa o erro TS2769
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip, // Import Chip for tags
} from "@mui/material";

// Importe o novo componente
import AtribuirCidades from "../AtribuirCidades"; // Ajuste o caminho conforme necessário

interface Usuario {
  id: number;
  name: string;
  email: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
}

// Interface para a cidade, com base no formato da sua API
interface City {
  id: string; // O ID da cidade é uma string na sua resposta
  name: string;
}

interface GerenciarAdminListaProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
  shouldUpdateList: boolean;
}

const GerenciarAdminLista: React.FC<GerenciarAdminListaProps> = ({
  token,
  role,
  shouldUpdateList,
}) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados para o diálogo de confirmação
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    "DELETE_USER" | "EDIT_ROLE" | null
  >(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [newRole, setNewRole] = useState<"PARTNER" | "ANALYST" | null>(null);

  // Novo estado para o diálogo de atribuição de cidades
  const [openAssignCitiesDialog, setOpenAssignCitiesDialog] = useState(false);

  // NOVO ESTADO: Armazena as cidades por ID de usuário (apenas para parceiros)
  const [partnerCities, setPartnerCities] = useState<Record<number, City[]>>(
    {},
  );
  const [fetchingCities, setFetchingCities] = useState<boolean>(false);

  const roleMap = {
    MASTER: "Master",
    PARTNER: "Parceiro",
    ANALYST: "Analista",
  };

  // NOVO: Função para buscar as cidades de um parceiro específico
  const fetchCitiesForPartner = async (userId: number) => {
    try {
      setFetchingCities(true);
      const response = await fetch(`${API_URLS.admincities}/${userId}/cities`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar cidades para o usuário ${userId}`);
      }

      const data: City[] = await response.json();
      setPartnerCities((prevCities) => ({
        ...prevCities,
        [userId]: data,
      }));
    } catch (err) {
      console.error(`Erro ao carregar cidades para o usuário ${userId}:`, err);
    } finally {
      setFetchingCities(false);
    }
  };

  const fetchUsuarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URLS.gerenciar, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Erro ao carregar usuários.");
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setUsuarios(data);
        // Após carregar os usuários, buscar cidades para os parceiros
        data.forEach((usuario: Usuario) => {
          if (usuario.role === "PARTNER") {
            fetchCitiesForPartner(usuario.id);
          }
        });
      } else {
        setSnackbarMessage("Dados de usuários inválidos recebidos");
        setSnackbarOpen(true);
      }
    } catch (err: any) {
      setSnackbarMessage(err.message || "Erro ao carregar usuários");
      setSnackbarOpen(true);
      setError(err.message || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [token, shouldUpdateList]);

  // Restante das funções (handleDeleteUser, handleChangeRole, handleConfirmAction, etc.)
  // permanecem inalteradas, exceto pela remoção do Grid.

  // Função para remover o usuário
  const handleDeleteUser = async (userId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URLS.gerenciar}/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Erro ao remover o usuário.");
      }

      setUsuarios((prevUsuarios) =>
        prevUsuarios.filter((usuario) => usuario.id !== userId),
      );
      setPartnerCities((prevCities) => {
        const newCities = { ...prevCities };
        delete newCities[userId];
        return newCities;
      });
      setSnackbarMessage("Usuário removido com sucesso!");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage("Erro ao remover o usuário.");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Atualizar o role de um usuário
  const handleChangeRole = async (
    id: number,
    novoRole: "PARTNER" | "ANALYST",
  ) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URLS.gerenciar}/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role: novoRole }),
      });

      if (!response.ok) {
        throw new Error("Erro ao atualizar o nível do usuário.");
      }

      setUsuarios((prevUsuarios) =>
        prevUsuarios.map((usuario) =>
          usuario.id === id ? { ...usuario, role: novoRole } : usuario,
        ),
      );

      if (novoRole === "PARTNER") {
        fetchCitiesForPartner(id);
      } else {
        setPartnerCities((prevCities) => {
          const newCities = { ...prevCities };
          delete newCities[id];
          return newCities;
        });
      }

      setSnackbarMessage("Nível do usuário atualizado com sucesso!");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage("Erro ao atualizar o nível.");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Função para abrir o diálogo de confirmação
  const openConfirmationDialog = (
    action: "DELETE_USER" | "EDIT_ROLE",
    userId: number | null,
    role?: "PARTNER" | "ANALYST",
  ) => {
    setConfirmAction(action);
    setSelectedUserId(userId);
    setNewRole(role || null);
    setOpenDialog(true);
  };

  // Função de confirmação
  const handleConfirmAction = () => {
    if (confirmAction === "DELETE_USER" && selectedUserId) {
      handleDeleteUser(selectedUserId);
    } else if (confirmAction === "EDIT_ROLE" && selectedUserId && newRole) {
      handleChangeRole(selectedUserId, newRole);
    }
    setOpenDialog(false);
  };

  // --- Funções para Atribuir Cidades ---
  const handleOpenAssignCities = (userId: number) => {
    setSelectedUserId(userId);
    setOpenAssignCitiesDialog(true);
  };

  const handleCloseAssignCitiesDialog = () => {
    setOpenAssignCitiesDialog(false);
    setSelectedUserId(null);
  };

  const handleSaveAssignedCities = async (
    userId: number,
    cityIds: number[],
  ) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URLS.admincities}/${userId}/cities`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ cityIds }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao atribuir cidades.");
      }

      setSnackbarMessage("Cidades atribuídas com sucesso!");
      setSnackbarOpen(true);
      handleCloseAssignCitiesDialog();
      // Converte os IDs de volta para strings (se a API retornar strings)
      // Nota: Idealmente, você buscaria novamente as cidades ou usaria o retorno da API.
      // Aqui, apenas disparamos a busca para ter os dados atualizados:
      fetchCitiesForPartner(userId);
    } catch (error: any) {
      setSnackbarMessage(`Erro ao atribuir cidades: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading && usuarios.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        Gerenciar Administradores
      </Typography>

      {/* Lista de admins cadastrados */}
      <Box
        sx={{
          mb: 3,
          width: "100%",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          // Espaçamento simulado: margin negativo para compensar o padding dos itens
          margin: "-12px",
        }}
      >
        {usuarios && Array.isArray(usuarios) && usuarios.length > 0 ? (
          usuarios.map((usuario) => (
            // Substitui o <Grid item> pelo <Box> com largura responsiva e padding
            <Box
              key={usuario.id}
              sx={{
                padding: "12px", // Simula o spacing={3}
                marginBottom: "20px",
                // Larguras para simular xs={12}, sm={6}, md={4}
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "30%", // 100% / 3
                },
              }}
            >
              <Box
                sx={{
                  border: "1px solid #ddd",
                  padding: 2,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Typography variant="h6" noWrap sx={{ fontWeight: "bold" }}>
                  {usuario.name}
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ color: "text.secondary" }}
                >
                  {usuario.email}
                </Typography>
                <Typography color="textSecondary" sx={{ marginTop: 1 }}>
                  Nível: {roleMap[usuario.role]}
                </Typography>

                {/* Renderização das cidades como tags */}
                {usuario.role === "PARTNER" && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      minHeight: "30px", // Evita quebra de layout durante o carregamento
                    }}
                  >
                    {partnerCities[usuario.id] &&
                    partnerCities[usuario.id].length > 0 ? (
                      partnerCities[usuario.id].map((city) => (
                        <Chip key={city.id} label={city.name} size="small" />
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {fetchingCities
                          ? "Carregando cidades..."
                          : "Nenhuma cidade atribuída."}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Select de Alterar Nível */}
                {usuario.role !== "MASTER" && (
                  <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                    <InputLabel>Alterar Nível</InputLabel>
                    <Select
                      value={usuario.role}
                      onChange={(e) =>
                        openConfirmationDialog(
                          "EDIT_ROLE",
                          usuario.id,
                          e.target.value as "PARTNER" | "ANALYST",
                        )
                      }
                      label="Alterar Nível"
                    >
                      <MenuItem value="PARTNER">Parceiro</MenuItem>
                      <MenuItem value="ANALYST">Analista</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Botão de Atribuir Cidades, visível apenas para PARTNER */}
                {usuario.role === "PARTNER" && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => handleOpenAssignCities(usuario.id)}
                  >
                    Atribuir Cidades
                  </Button>
                )}

                <Button
                  variant="outlined"
                  color="error"
                  disabled={usuario.role === "MASTER"}
                  sx={{ mt: 2 }}
                  onClick={() =>
                    openConfirmationDialog("DELETE_USER", usuario.id)
                  }
                >
                  Remover
                </Button>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body1" color="textSecondary">
            Nenhum usuário encontrado.
          </Typography>
        )}
      </Box>

      {/* Snackbar de sucesso ou erro (inalterado) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Dialog de Confirmação (inalterado) */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {confirmAction === "DELETE_USER"
            ? "Confirmar Remoção"
            : "Confirmar Alteração de Nível"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {confirmAction === "DELETE_USER"
              ? "Tem certeza de que deseja remover este usuário?"
              : "Tem certeza de que deseja alterar o nível deste usuário?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color="primary"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Novo Dialog para Atribuir Cidades (inalterado) */}
      <Dialog
        open={openAssignCitiesDialog}
        onClose={handleCloseAssignCitiesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogContent dividers>
          {selectedUserId && (
            <AtribuirCidades
              userId={selectedUserId}
              token={token}
              onSave={handleSaveAssignedCities}
              onClose={handleCloseAssignCitiesDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GerenciarAdminLista;
